/**
 * Context-IR client — browser-side caller for /api/context-ir
 *
 * Mirrors H3's Context-IR concept: a dedicated understanding pass that runs
 * before the main generation step. The harness calls this before sending
 * anything to the LLM, receives a structured ContextIR object, and uses
 * the enriched_prompt + tools_hint to steer the main turn.
 *
 * Architecture note from MiniMax-H3:
 *   H3-Context-IR serialises multimodal relationships into a structured
 *   intermediate representation that the generator (H3-Base) consumes instead
 *   of the raw prompt. The generator is freed from interpretation work and
 *   can focus purely on generation. The same principle applies here: the main
 *   LLM is freed from audience-detection, intent-classification, and section-
 *   routing, because Context-IR has already done that work.
 */

export interface ContextIR {
  /** What the visitor actually wants, in one sentence */
  intent: string;
  /** Detected visitor segment */
  audience: 'recruiter' | 'technical_peer' | 'casual' | 'unknown';
  /** Whether this question warrants a quick focused answer */
  urgency: 'high' | 'normal';
  /** Site sections most relevant to answering */
  relevant_sections: string[];
  /** Tools the harness should bias toward */
  tools_hint: string[];
  /** Original message rewritten with richer context for the answering LLM */
  enriched_prompt: string;
  /** How the answering agent should calibrate its response length/style */
  tone_hint: 'concise' | 'detailed' | 'playful';
  /** Tokens used in the context-ir pass (for observability) */
  _tokens?: number;
  /** Non-null when the pass degraded gracefully */
  _error?: string;
}

/**
 * Run the Context-IR preprocessing pass.
 *
 * Always resolves — never throws. If the API is unavailable or returns
 * malformed output, returns a passthrough IR so the harness can continue.
 *
 * @param message  The raw visitor message
 * @param pageTitle  Current page, if available
 * @param historySummary  Last 1-2 turns compressed to a short string
 * @param signal  AbortSignal to cancel if the user types again
 */
export async function runContextIR(
  message: string,
  pageTitle?: string,
  historySummary?: string,
  signal?: AbortSignal,
): Promise<ContextIR> {
  // Build a compressed history summary (last 2 exchanges max)
  try {
    const res = await fetch('/api/context-ir', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        page_title: pageTitle,
        history_summary: historySummary,
      }),
      signal,
    });

    if (!res.ok) {
      return passthrough(message);
    }

    const data: ContextIR = await res.json();
    return data;
  } catch {
    return passthrough(message);
  }
}

/**
 * Compress last N chat turns into a short string for history context.
 * Keeps the IR request small — this is a preprocessing step, not a full replay.
 */
export function compressHistory(
  messages: Array<{ role: string; content: string }>,
  maxTurns = 2,
): string {
  const recent = messages
    .filter(m => m.role === 'user' || m.role === 'assistant')
    .slice(-maxTurns * 2);

  return recent
    .map(m => `${m.role === 'user' ? 'visitor' : 'agent'}: ${m.content.slice(0, 120)}`)
    .join(' | ');
}

function passthrough(message: string): ContextIR {
  return {
    intent: message,
    audience: 'unknown',
    urgency: 'normal',
    relevant_sections: [],
    tools_hint: [],
    enriched_prompt: message,
    tone_hint: 'concise',
    _error: 'passthrough',
  };
}
